import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { usePagination } from "src/hooks/usePagination";
import { useOutsideClick } from "src/hooks/useOutsideClick";
import { usePoHandlers } from "../PurchaseOrder/PoController";
import { useToast } from "src/hooks/useToast";
import { SortingState } from "@tanstack/react-table";
import { Transition } from "@headlessui/react";
import * as yup from "yup";
import { RootState } from "src/context/store";
import StockistBlockLayout from "./Stockist-block-layout.component";
import Input from "../../components/Input";
import Button from "src/features/fields/components/Button";
import Table from "src/features/fields/components/Table";
import Badge from "src/features/fields/components/Badge";
import POFilter from "src/features/fields/container/POFilter";
import DeleteModal from "src/container/DeleteModal";
import { createColumnHelper } from "@tanstack/react-table";
import FilterIcon from "src/assets/filter.svg";
import moment from "moment";
import { getStockistBlockList, updateStockistBlockRevoke } from "src/services/stockist-block-api"
import { queryDivisionDetails } from "src/services/division-api";
import { getAllCompanyDetails } from "src/services/company-api";
import { poActions } from "src/context/po-slice";
import { appendParams } from "src/utils/utils";

const status = [{ name: "Blocked", value: "blocked" }, { name: "Revoked", value: "revoked" }]

const blockType = [{ name: "EInv_Ack Pending", value: 1 }, { name: "SAO Block", value: 2 }, { name: "Others", value: 3 }]

const StockistBlockTab = () => {
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false)
  const token: any = useSelector((state: RootState) => state.auth);
  const { stockistBlockTableData, poFiltersData, stockistBlockFilters } = useSelector((state: any) => state.po);
  const { pagination, onPaginationChange, pageIndex, pageSize, resetPagination } = usePagination();
  const [totalRowCount, setTotalRowCount] = useState(10);
  const [isFiltersShown, setIsFiltersShown] = useState<boolean>(false);
  const [sorting, setSorting] = useState<SortingState>();
  const [globalSearch, setGlobalSearch] = useState("");
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [SelectedStockist, setSelectedStockist] = useState<{ [key: string]: any } | null>(null);
  const dispatch = useDispatch();

  const ref = useOutsideClick(() => {
    setIsFiltersShown(false);
  });

  const [_, setSearchParams] = useSearchParams();

  const { toast } = useToast();

  const revokeSchema = yup.object().shape({
    reference_number: yup.string().required("Reference number is required").max(150, "Remarks must not exceed 150 characters"),
  });

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor("stockist_metadata.company.companyName", {
      header: () => "Company Name",
      enableSorting: true,
      cell: (info: any) => info.getValue() || "-",
    }),
    columnHelper.accessor("stockist_metadata.divisions.divisionName", {
      header: () => "Division Name",
      enableSorting: true,
      cell: (info: any) => info.getValue() || "-",
    }),
    columnHelper.accessor("stockist_metadata.stockists.firm_name", {
      header: () => "Stockist Name",
      enableSorting: true,
      cell: (info: any) => info.getValue() || "-",
    }),
    columnHelper.accessor("stockist_metadata.stockists.medvol_code", {
      header: () => "Stockist Code",
      enableSorting: true,
      cell: (info: any) => info.getValue() || "-",
    }),
    columnHelper.accessor("company_stockist_code", {
      header: () => "Company Stockist Code",
      enableSorting: true,
      cell: (info: any) => info.getValue() || "-",
    }),
    columnHelper.accessor("stockist_metadata.products.product_name", {
      header: () => "Product Name",
      enableSorting: true,
      cell: (info: any) => info.getValue() || "-",
    }),
    columnHelper.accessor("stockistBlockHdr.remarks_desc", {
      header: () => "Block Type",
      enableSorting: true,
      cell: (info: any) => info.getValue() || "-",
    }),
    columnHelper.accessor("reason", {
      header: () => "Remarks",
      enableSorting: true,
      cell: (info: any) => info.getValue() || "-",
    }),
    columnHelper.accessor("is_blocked", {
      header: () => "Status",
      enableSorting: true,
      cell: (info: any) => {
        const isBlocked = Boolean(info.getValue());
        return (
          <Badge variant={isBlocked ? "error" : "success"} className="capitalize">
            {isBlocked ? "Blocked" : "Revoked"}
          </Badge>
        );
      },
    }),
    columnHelper.accessor("created_date", {
      header: () => "Blocked Date",
      enableSorting: true,
      cell: (info: any) => {
        const { is_blocked, created_date, modified_date } = info.row.original;
        const dateToShow = is_blocked ? created_date : modified_date;

        return dateToShow
          ? moment(dateToShow)
            .tz("Asia/Kolkata")
            .format("DD MMM YYYY")
          : "-";
      },
    }),
    columnHelper.accessor("actions", {
      header: () => <span className="ml-auto mr-2">Stockist Revoke</span>,
      enableSorting: false,
      size: 124,
      cell: ({ row }: any) =>
        <div className="flex justify-end w-full">
          <Button
            disabled={row?.original?.active_status === "N"}
            className={row?.original?.active_status === "N" ? `bg-gray-100 px-6 border` : `px-6 border`}
            onClick={() => {
              revokeFunctionHandler(row.original);
            }}
          >
            Revoke
          </Button>

        </div>
    }),
  ];

  const revokeFunctionHandler = async (rowData: any) => {
    setShowRevokeModal(true);
    setSelectedStockist(rowData);
  };

  const handleStockistRevoke = async (remarksDetails: any) => {
    if (!SelectedStockist) {
      toast({ description: "No stockist selected." });
      return;
    }

    const {
      company_stockist_code,
      company_code,
      division_code,
      item_code,
      stockistBlockHdr,
    } = SelectedStockist;

    const created_by = token?.token?.sub;
    const reason = remarksDetails?.reference_number;

    if (!reason) {
      toast({ description: "Remarks is required." });
      return;
    }

    const payload = {
      data: {
        company_stockist_code,
        company_code,
        division_code,
        item_code,
        created_by,
        reason,
        is_blocked: false,
        remarks_id: stockistBlockHdr?.remarks_id,
        target: "V2",
      },
    };

    try {
      setIsLoading(true);

      const res = await updateStockistBlockRevoke(payload, company_code);

      if (res?.status === 200) {
        if (res?.data?.status === false) {
          toast({ description: res?.data?.message || "Unable to revoke stockist." });
        } else {
          toast({ description: "Stockist revoked successfully." });
          setSelectedStockist(null);
          setTimeout(() => {
            fetchDetails();
          }, 2000);
        }
      } else {
        console.error("Failed to revoke the stockist:", res?.data);
        toast({ description: res?.data?.message || "Failed to revoke stockist.", variant: "destructive" });
      }
    } catch (err: any) {
      console.error("Error revoking stockist:", err);
      toast({
        description:
          err?.response?.data?.message ||
          err?.message ||
          "An unexpected error occurred while revoking stockist.",
      });
    } finally {
      setShowRevokeModal(false);
      setIsLoading(false);
    }
  };

  const fetchDetails = () => {
    setLoading(true);

    setSearchParams({
      pageSize: pageSize.toString(),
      pageIndex: pageIndex.toString(),
    });

    const companyParams = appendParams(Array.isArray(stockistBlockFilters?.company) ? stockistBlockFilters.company : [], "company", "code");
    const divisionParams = appendParams(
      Array.isArray(stockistBlockFilters?.division) ? stockistBlockFilters.division : [],
      "division",
      "company_divisioncode"
    );

    const blockTypeParams = appendParams(
      Array.isArray(stockistBlockFilters?.blockType) ? stockistBlockFilters.blockType : [],
      "blockType",
      "value"
    );


    let filtersQuery = [companyParams, divisionParams, blockTypeParams].join("&");

    filtersQuery.endsWith("&&") && (filtersQuery = filtersQuery.slice(0, -1));
    filtersQuery.endsWith("&") && (filtersQuery = filtersQuery.slice(0, -1));

    const selectedStatuses = stockistBlockFilters?.status || [];

    const hasBlocked = selectedStatuses.some((s: any) => s.value === "blocked");
    const hasRevoked = selectedStatuses.some((s: any) => s.value === "revoked");

    let is_blocked: boolean | undefined = undefined;

    if (hasBlocked && !hasRevoked) {
      is_blocked = true;
    } else if (!hasBlocked && hasRevoked) {
      is_blocked = false;
    } else {
      is_blocked = undefined;
    }

    getStockistBlockList(
      is_blocked,
      pageIndex - 1,
      pageSize,
      filtersQuery,
      globalSearch ? globalSearch : undefined
    )
      .then((res) => {
        dispatch(poActions.setStockistBlockTableData(res?.data?.data?.data || []));
        setTotalRowCount(res?.data?.data?.totalDataCount);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDetails();
  }, [pageSize, pageIndex, sorting, globalSearch]);

  let pageCount = Math.ceil(totalRowCount / (pageSize ?? 1));
  if (totalRowCount % 10 === 0) {
    pageCount--;
  }

  let timerId: any;
  const handleSearch = (searchQuery: string) => {
    if (timerId) clearTimeout(timerId);

    timerId = setTimeout(() => {
      resetPagination();
      setGlobalSearch(searchQuery);
    }, 500);
  };

  const {
    fetchState,
  } = usePoHandlers();

  useEffect(() => {
    getAllCompanyDetails()
      .then((res) =>
        dispatch(poActions.setPoFiltersData({ key: "company", value: res.data }))
      )
      .catch((err) => console.log(err));

    if (stockistBlockFilters?.company?.length) {
      queryDivisionDetails(
        `/details/?fields=division_name&company_code=${stockistBlockFilters.company[0].code}`
      )
        .then((res) =>
          dispatch(poActions.setPoFiltersData({ key: "stockistBlockDivision", value: res.data }))
        )
        .catch((err) => console.log(err));
    } else {
      dispatch(poActions.setPoFiltersData({ key: "stockistBlockDivision", value: [] }));
      dispatch(poActions.setStockistBlockFilters({ key: "division", value: [] }));
    }

    fetchState?.();
  }, [stockistBlockFilters?.company, dispatch]);


  return (
    <StockistBlockLayout>
      <Transition
        as={Fragment}
        show={isFiltersShown}
        enter="transition transition ease-in-out duration-200 transform"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transition  ease-in-out duration-200 transform"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <div className="fixed right-0 inset-0 top-0 z-[500]">
          <POFilter
            ref={ref}
            companyDetails={poFiltersData?.company || []}
            divisionDetails={poFiltersData?.stockistBlockDivision || []}
            statusDetails={status}
            blockTypeDetails={blockType}
            source="StockistBlock"
            onCloseClick={() => setIsFiltersShown(false)}
          />
        </div>
      </Transition>

      <section>
        <header className="flex px-[3.75rem] items-center justify-between py-5 border-b-[1px] border-b-[#E9E9E9]">
          <div>
            <h1 className="text-[#49484C] font-bold text-[2rem]">Stockist Block Requests</h1>
            <p className="font-medium text-base text-[#929194]">View and manage all your stockist block-revoke requests.</p>
          </div>

          <div className="flex items-center gap-2 ">
            <Input
              placeholder="Search"
              className="py-[0.7rem] px-3 rounded-lg"
              showIcon={true}
              value={globalSearch}
              onChange={(e) => handleSearch(e.target.value)}
            />
            <Button
              className="relative flex items-center gap-1 px-4 py-3 border"
              onClick={() => {
                setIsFiltersShown(true);
              }}
            >
              Filters <img src={FilterIcon} className="w-5 h-5" />
              {stockistBlockFilters?.filterCount > 0 && (
                <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 text-xs text-white bg-red-500 rounded-full">
                  {stockistBlockFilters?.filterCount}
                </span>
              )}
            </Button>
            <Button onClick={fetchDetails} loaderSize="medium"
              className="bg-[#F3EFFC]  px-4 border-2 text-[#42267F] border-[#7F56D9] ml-auto font-semibold text-base"
            >
              Retrieve
            </Button>
          </div>
        </header>

        <div className="px-[3.75rem] mt-6">
          <Table
            columns={columns}
            data={stockistBlockTableData ?? []}
            isLoading={loading}
            manualPagination={true}
            pageCount={pageCount}
            pagination={pagination}
            onPaginationChange={onPaginationChange}
            sorting={sorting}
            onSortingChange={setSorting}
            manualSorting={true}
            showSearch={false}
          />
        </div>
      </section>

      <DeleteModal title="Stockist Revoke" label="Remarks" schema={revokeSchema} value={showRevokeModal} deleteText="Continue" setValue={setShowRevokeModal} loading={isLoading} onFormSubmit={handleStockistRevoke} />
    </StockistBlockLayout>
  );
};

export default StockistBlockTab;
